import { ActivatedRoute, Route as AngularRoute, RouterState } from '@angular/router';
import { ICrumb, IMetaData, IModelDataV3WithUrl, Model } from '@df/catalog/model';
import { Route } from '@df/catalog/route/route';
import { App } from '@df/core/app';
import { NgInject } from '@df/ng/ng-inject';
import { TranslationService } from '@df/ui/translation/translation.service';
import { isEqual } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import last from 'lodash-es/last';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NCore } from '../df-core.interfaces';
import { Product } from '@df/catalog/product/product';

export enum HTTPStatus {
    ok = 200,
    redirect = 301,
    notfound = 404,
    error = 500
}

export const ON_STATE_META_SET = 'ON_STATE_META_SET';
export interface IOnStateMetaSetEvent {
    metaData: IMetaData;
    metaState: MetaState;
}

export interface IMetaDataExtended extends IMetaData {
    pageType?: string;
    title?: string;
    robots?: string;
}
/**
 * Constant to inject static meta data
 */

export const STATIC_META: { [url: string]: IMetaDataExtended } = {};
export const ROUTE_OVERRIDE: { [path: string]: string } = {};

STATIC_META['/account'] = {
    title: 'My account',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/edit'] = {
    title: 'Account information',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/address-book'] = {
    title: 'Address book',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/address'] = {
    title: 'Address edit',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/my-orders'] = {
    title: 'My orders',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/orders'] = {
    title: 'Order details',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/basket'] = {
    title: 'Basket',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'basket'
};

STATIC_META['/checkout'] = {
    title: 'Secure checkout',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'checkout'
};

STATIC_META['/checkout/success'] = {
    title: 'Secure checkout',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'checkout'
};

STATIC_META['/login'] = {
    title: 'Sign in',
    robots: 'INDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/login/reset-password'] = {
    title: 'Set up your new password',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/saved-cards'] = {
    title: 'My Saved Cards',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/wishlist'] = {
    title: 'My Wishlist',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/order/:id'] = {
    title: 'My Order',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/my-orders/:id'] = {
    title: 'My Orders',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/account/address/:id'] = {
    title: 'Address edit',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'account'
};

STATIC_META['/search/**'] = {
    title: 'Search Results',
    robots: 'NOINDEX,FOLLOW',
    pageType: 'search'
};

STATIC_META['error:404'] = {
    title: 'Page not found'
};

STATIC_META['error:500'] = {
    title: 'Unexpected error',
    robots: 'NOINDEX,FOLLOW'
};

export class MetaState {
    /**
     * Force model
     */
    private _model?: Model<IModelDataV3WithUrl>;
    /**
     * Forced meta data
     */
    protected _metaDataForced?: IMetaData | IMetaDataExtended;
    /**
     * Current meta data
     */
    protected _metaData?: IMetaData | IMetaDataExtended;

    /**
     * Router config path buffer
     */
    private _routerConfigPath?: string;

    /**
     * Route buffer
     */
    private _route?: Route;

    /**
     * Breadcrumbs observable
     */
    readonly breadcrumbs$ = new BehaviorSubject<ICrumb[] | undefined>(undefined);

    /**
     * Model observable
     */
    readonly model$ = new BehaviorSubject<Model<IModelDataV3WithUrl> | undefined>(undefined);

    private modelChangeSubscription?: Subscription;

    @NgInject(App)
    protected app!: App;

    @NgInject(TranslationService)
    protected translationService!: TranslationService;

    /**
     * Class constructor
     * @param url Page url
     * @param previousState previous state
     */
    constructor(
        private options: NCore.IModuleOptions,
        public url: string,
        route: Route | undefined,
        private routerState: RouterState,
        private previousState?: MetaState,
        private forcedStatus?: HTTPStatus
    ) {
        if (route) {
            this.route = route;
        }
        this.model$.subscribe(model => {
            const update = () => {
                this.updateBreadcrumbs();
                this.updateMetaData();
            };

            if (model) {
                this.modelChangeSubscription = model.change.subscribe(update);
            }
            update();
        });
    }

    destroy() {
        this.modelChangeSubscription?.unsubscribe();
    }

    /**
     * Clear buffered data
     */
    updateBreadcrumbs(): void {
        const breadcrumbs = this.getBreadcrumbs();
        if (!isEqual(breadcrumbs, this.breadcrumbs$.value)) {
            this.breadcrumbs$.next(breadcrumbs);
        }
    }

    /**
     * Force model setter
     */
    set model(model: Model<IModelDataV3WithUrl> | undefined) {
        this.model$.next(model);
    }

    /**
     * State model getter
     */
    get model(): Model<IModelDataV3WithUrl> | undefined {
        return this.model$.value;
    }

    set route(route: Route) {
        this._route = route;
        if (!this.model$.value) {
            const model = route?.target;
            if (model) {
                this.model$.next(model);
            }
        }
    }

    get route(): Route | undefined {
        return this._route;
    }

    /**
     * URL path
     */
    get path(): string {
        return this.url;
    }

    /**
     * Forced metadata setter
     */
    set metaData(metaData: IMetaData | IMetaDataExtended | undefined) {
        this._metaDataForced = metaData;
        this.updateMetaData();
    }

    /**
     * Current meta data getter
     */
    get metaData(): IMetaData | IMetaDataExtended | undefined {
        return this._metaData;
    }

    get pageType(): string | undefined {
        const meta = this.metaData;
        if (meta && 'pageType' in meta) {
            return meta.pageType;
        }
        const model = this.model;
        if (model) {
            return model.type;
        }
        return undefined;
    }

    updateMetaData() {
        const getMetData = () => {
            if (this._metaDataForced) {
                return this._metaDataForced;
            }
            const status = this.status;
            if (status === HTTPStatus.ok) {
                if (STATIC_META[this.path]) {
                    return STATIC_META[this.path];
                }
                const routerConfigPath = this.getRouterConfigPath();
                if (routerConfigPath) {
                    const routerConfigStaticMeta = STATIC_META[`/${routerConfigPath}`];
                    if (routerConfigStaticMeta) {
                        return routerConfigStaticMeta;
                    }
                }
                return this.model?.rawData?.meta;
            } else {
                return STATIC_META['error:' + status];
            }
        };

        const event: IOnStateMetaSetEvent = { metaData: { ...getMetData() }, metaState: this };
        this.app.events.broadcast(ON_STATE_META_SET, event);
        event.metaData.title = event.metaData.title && this.translationService.translate(event.metaData.title);
        this._metaData = event.metaData;
    }

    getRouterConfigPath(): string | undefined {
        if (!this._routerConfigPath) {
            const prepareRouterConfigPath = () => {
                let sucurityLimit = 0;

                const getRouteConfigsFromActivatedRoute = (route: ActivatedRoute, configs: AngularRoute[] = []) => {
                    sucurityLimit++;
                    if (route.routeConfig) {
                        configs.push(route.routeConfig);
                    }
                    if (route.firstChild && sucurityLimit < 10) {
                        return getRouteConfigsFromActivatedRoute(route.firstChild, configs);
                    } else {
                        return configs;
                    }
                };

                if (this.routerState?.root?.firstChild) {
                    const configs = getRouteConfigsFromActivatedRoute(this.routerState.root.firstChild, []) as AngularRoute[];
                    return compact(compact(configs).map(config => config.path)).join('/');
                }
                return undefined;
            };
            this._routerConfigPath = prepareRouterConfigPath();
        }
        return this._routerConfigPath;
    }

    /**
     * Https status getter
     */
    get status(): HTTPStatus {
        if (this.forcedStatus) {
            return this.forcedStatus;
        }
        const route = this.route;
        if (!route) {
            return HTTPStatus.ok;
        }
        if (route.rawData?.rewrite) {
            return HTTPStatus.redirect;
        } else if (route.data && !route.target) {
            return HTTPStatus.notfound;
        } else {
            return HTTPStatus.ok;
        }
    }

    /**
     * Raw breadcrumbs getter without previous state check
     */
    get rawBreadcrumbs(): ICrumb[] | undefined {
        let model = this.model;
        if (model?.type === 'product' && this.options.breadcrumbs.productMode === 'parent') {
            model = (model as Product).parent as Model<IModelDataV3WithUrl, number>;
        }
        const data = model?.rawData;
        if (data?.breadcrumbs) {
            const crumbs = [...data.breadcrumbs];
            const homeTitle = this.options.breadcrumbs.homeTitle;
            if (homeTitle) {
                if (crumbs[0]?.url === '/') {
                    crumbs.shift();
                }
                crumbs.unshift({ url: '/', label: homeTitle });
            }
            const lastCrumb = last(crumbs);
            const lastIsSelf = lastCrumb ? lastCrumb.url === data.url : false;
            if (this.options.breadcrumbs.skipLastCrumb) {
                return lastIsSelf ? crumbs.slice(crumbs.length - 1) : crumbs;
            } else {
                return lastIsSelf ? crumbs : [...crumbs, { url: data.url, label: model!.name }];
            }
        }
        return undefined;
    }

    /**
     * Current breadcrumbs getter
     */
    get breadcrumbs(): ICrumb[] | undefined {
        return this.breadcrumbs$.value;
    }

    getBreadcrumbs(): ICrumb[] | undefined {
        const rawBreadcrumbs = this.rawBreadcrumbs;
        if (this.previousState && !this.options.breadcrumbs.ignoreEntryPage) {
            const previousModel = this.previousState.model;
            const currentModel = this.model;

            if (previousModel && currentModel) {
                // try to use last category path for product breadcrumbs
                if (
                    (currentModel.type === 'product' && previousModel.type === 'category') ||
                    (currentModel.type === 'page' && previousModel.type === 'page' && (<any>currentModel).parent?.id === previousModel.id)
                ) {
                    const buffer = cloneDeep(this.previousState.breadcrumbs);
                    const lastCrumb = last(rawBreadcrumbs);
                    if (buffer && lastCrumb) {
                        buffer.push(lastCrumb);
                        return buffer;
                    }
                }
            }
        }
        return rawBreadcrumbs;
    }

    get robots(): string {
        return this.metaData?.robots || 'INDEX,FOLLOW';
    }
}

export const metaStateFactory = {
    instance: MetaState
};
